import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { formatError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

export default function AuthPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const { login, register } = useAuth();
  const params = new URLSearchParams(loc.search);
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [pwdError, setPwdError] = useState("");

  useEffect(() => {
    setMode(params.get("mode") === "register" ? "register" : "login");
    // eslint-disable-next-line
  }, [loc.search]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && password.length < 6) {
      setPwdError("6 caractères minimum.");
      return;
    }
    setLoading(true);
    try {
      const user = mode === "login" ? await login(email, password) : await register(email, password, name);
      toast.success(mode === "login" ? "Connexion réussie" : "Compte créé");
      if (user.role === "coach") nav("/coach");
      else if (user.has_profile) nav("/app");
      else nav("/onboarding");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const titles = {
    login: ["Connexion", "Accède à ton programme."],
    register: ["Créer un compte", "Rejoins la méthode Roxlab."],
    forgot: ["Mot de passe oublié", "Reçois un lien de réinitialisation par email."],
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-background">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8 cursor-pointer" onClick={() => nav("/")}>
          <Flame className="text-primary" size={28} strokeWidth={2.5} />
          <span className="font-display text-3xl">ROX<span className="text-primary">LAB</span></span>
        </div>
        <div className="bg-card border border-border rounded-lg p-6 md:p-8">
          <h1 className="font-display text-3xl mb-1">{titles[mode][0]}</h1>
          <p className="text-sm text-muted-foreground mb-6">{titles[mode][1]}</p>

          {mode === "forgot" ? (
            forgotSent ? (
              <div data-testid="forgot-sent" className="text-center py-2">
                <p className="text-sm text-muted-foreground mb-5">
                  Si un compte existe pour <b className="text-foreground">{email}</b>, un email contenant un lien de réinitialisation vient d'être envoyé. Pense à vérifier tes spams. Le lien expire dans 1 heure.
                </p>
                <Button data-testid="forgot-back-btn" onClick={() => { setMode("login"); setForgotSent(false); }} className="bg-primary hover:bg-primary/90 text-white rounded-md h-12 px-6 font-semibold">Retour à la connexion</Button>
              </div>
            ) : (
              <form onSubmit={submitForgot} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                  <Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12 text-base mt-1 bg-secondary border-border" placeholder="toi@email.com" />
                </div>
                <Button data-testid="forgot-submit-btn" type="submit" disabled={loading} className="w-full h-13 py-3 bg-primary hover:bg-primary/90 text-white rounded-md text-base font-semibold active:scale-95 transition-transform">
                  {loading ? "…" : "Envoyer le lien"}
                </Button>
                <p className="text-sm text-muted-foreground text-center">
                  <button type="button" data-testid="forgot-to-login-btn" className="text-primary font-semibold" onClick={() => setMode("login")}>Retour à la connexion</button>
                </p>
              </form>
            )
          ) : (
            <>
              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nom</Label>
                    <Input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="h-12 text-base mt-1 bg-secondary border-border" placeholder="Ton nom" />
                  </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
                  <Input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-12 text-base mt-1 bg-secondary border-border" placeholder="toi@email.com" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Mot de passe</Label>
                    {mode === "login" && (
                      <button type="button" data-testid="forgot-password-link" className="text-xs text-primary font-semibold" onClick={() => { setForgotSent(false); setMode("forgot"); }}>Oublié ?</button>
                    )}
                  </div>
                  <div className="relative mt-1">
                    <Input data-testid="auth-password-input" type={showPwd ? "text" : "password"} value={password} onChange={(e) => { setPassword(e.target.value); if (pwdError) setPwdError(""); }} required className="h-12 text-base pr-12 bg-secondary border-border" placeholder="••••••" />
                    <button type="button" data-testid="auth-password-toggle" onClick={() => setShowPwd((v) => !v)} aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"} className="absolute inset-y-0 right-0 flex items-center px-3.5 text-muted-foreground hover:text-foreground">
                      {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  {mode === "register" && (
                    <p data-testid="auth-password-error" className={`text-xs mt-1.5 ${pwdError ? "text-primary" : "text-muted-foreground"}`}>{pwdError || "6 caractères minimum"}</p>
                  )}
                </div>
                <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full h-13 py-3 bg-primary hover:bg-primary/90 text-white rounded-md text-base font-semibold active:scale-95 transition-transform">
                  {loading ? "…" : mode === "login" ? "Se connecter" : "Créer mon compte"}
                </Button>
              </form>
              <p className="text-sm text-muted-foreground mt-5 text-center">
                {mode === "login" ? "Pas encore de compte ?" : "Déjà inscrit ?"}{" "}
                <button data-testid="auth-toggle-btn" className="text-primary font-semibold" onClick={() => setMode(mode === "login" ? "register" : "login")}>
                  {mode === "login" ? "Créer un compte" : "Se connecter"}
                </button>
              </p>
            </>
          )}
        </div>
        <div className="mt-4 text-center text-xs text-muted-foreground space-x-3">
          <Link data-testid="auth-legal-link" to="/legal" className="hover:text-foreground">Mentions légales</Link>
          <span>·</span>
          <Link data-testid="auth-privacy-link" to="/privacy" className="hover:text-foreground">Confidentialité</Link>
        </div>
      </div>
    </div>
  );
}
